import { motion, useReducedMotion } from 'framer-motion';
import { Check, MapPin, Lock } from 'lucide-react';

interface WaypointProps {
  x: number;
  y: number;
  index: number;
  status: 'completed' | 'active' | 'locked';
  label: string;
  icon?: string;
  onClick?: () => void;
}

const Waypoint = ({ x, y, index, status, label, onClick }: WaypointProps) => {
  const prefersReducedMotion = useReducedMotion();
  const noRepeat = prefersReducedMotion ? 0 : Infinity;
  const isClickable = status === 'completed' || status === 'active';

  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && isClickable && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: isClickable ? 'pointer' : 'default' }}
      role="button"
      aria-label={`Step ${index + 1}: ${label} (${status})`}
      tabIndex={isClickable ? 0 : -1}
    >
      {/* Glow ring for active waypoint */}
      {status === 'active' && (
        <>
          <motion.circle
            r={22}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
            transition={{ duration: 1.5, repeat: noRepeat, ease: 'easeOut' }}
          />
          <motion.circle
            r={18}
            fill="hsl(var(--primary) / 0.15)"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: noRepeat, ease: 'easeInOut' }}
          />
        </>
      )}

      {/* Main circle */}
      <motion.circle
        r={status === 'active' ? 16 : status === 'completed' ? 12 : 10}
        fill={
          status === 'active'
            ? 'hsl(var(--primary))'
            : status === 'completed'
            ? 'hsl(var(--primary))'
            : 'hsl(var(--muted))'
        }
        stroke={
          status === 'active'
            ? 'hsl(var(--primary))'
            : status === 'completed'
            ? 'hsl(var(--primary) / 0.6)'
            : 'hsl(var(--border))'
        }
        strokeWidth={2}
        initial={false}
        animate={{ scale: status === 'active' ? [1, 1.05, 1] : 1 }}
        transition={status === 'active' ? { duration: 2, repeat: noRepeat } : {}}
      />

      {/* Icon inside circle */}
      {status === 'active' && (
        <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
          <MapPin x={-6} y={-7} width={12} height={14} className="text-primary-foreground" fill="currentColor" />
        </motion.g>
      )}
      {status === 'completed' && (
        <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
          <Check x={-6} y={-6} width={12} height={12} className="text-primary-foreground" strokeWidth={3} />
        </motion.g>
      )}
      {status === 'locked' && (
        <Lock x={-5} y={-5} width={10} height={10} className="text-muted-foreground/40" />
      )}

      {/* Step number for locked */}
      {status === 'locked' && (
        <text
          y={24}
          textAnchor="middle"
          className="fill-muted-foreground/30 text-[9px] font-medium"
        >
          {index + 1}
        </text>
      )}

      {/* Label for active waypoint */}
      {status === 'active' && (
        <motion.text
          y={-24}
          textAnchor="middle"
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: -24 }}
          className="fill-primary text-[10px] font-display font-semibold"
        >
          {label}
        </motion.text>
      )}
    </g>
  );
};

export default Waypoint;
